import React, { Fragment } from 'react';
import { Box, Button, Card, Flex, Heading, Link, Text } from 'rebass';
import { compose, withApollo } from 'react-apollo';
import { withWizard } from 'react-albus';
import gql from 'graphql-tag';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import styled from 'styled-components';

import { TextInput } from '../../components/TextInput';
import { FormField } from '../../components/FormField';
import { CheckboxInput } from '../../components/CheckboxInput';
import { MaskInput } from '../../components/MaskInput';
import Aside from './Aside';

const StyledLink = styled(Link)`
  color: #000;
`;

const Basic = (props) => {
  const { isNewPatientFlow, email } = props;
  const minDob = moment()
    .subtract(18, 'years')
    .toDate();

  const maxDob = moment()
    .subtract(125, 'years')
    .toDate();

  const submitCheck = async (values, actions) => {
    actions.setSubmitting(false);
    props.onSubmit(
      {
        dob: values.dob,
        password: values.password,
        consent: values.consent,
        consenthipaa: values.consenthipaa,
      },
      actions,
      props.wizard,
    );
  };

  const schema = Yup.object().shape({
    // email: Yup.string()
    //   .email('Invalid email')
    //   .required('Required'),
    password: Yup.string()
      .trim()
      .min(8, 'Password must be greater than 8 characters')
      .required('Required'),
    dob: Yup.date()
      .transform((value) => {
        return moment(value).toDate();
      })
      .min(maxDob, 'Must be younger than 125 years')
      .max(minDob, 'Must be 18 years or older')
      .required('Required'),
    consent: Yup.bool()
      .test('consent', 'You must agree to the Terms and Conditions', (val) => val === true)
      .required('You must agree to the Terms and Conditions'),
    consenthipaa: Yup.bool()
      .test('consenthipaa', 'You must agree to the Notice of Privacy Practices', (val) => val === true)
      .required('You must agree to the Notice of Privacy Practices'),
    consentshare: Yup.bool()
      .test('consentshare', 'You must consent to share your medical information with our physicians', (val) => val === true)
      .required('You must consent to share your medical information with our physicians'),
  });

  return (
    <Fragment>
      <Flex mb={4} flex={1} flexDirection={['column', 'column', 'row']}>
        <Flex flex={1} mr={[0, 0, 4]} mb={[4, 4, 0]}>
          <Card p={4}>
            <Flex flexDirection="column">
              <Box mb={4}>
                <Heading textAlign="center" mb={3}>
                  {isNewPatientFlow ? 'Create Your Account' : 'Get Started'}
                </Heading>
                {isNewPatientFlow ? <Text textAlign="center">We are holding your spot. Create an account to confirm it</Text> : <Text textAlign="center">Creating an account is free!</Text>}
              </Box>
              <Formik
                isInitialValid={({ initialValues }) => schema.isValidSync(initialValues)}
                initialValues={props.values}
                validationSchema={schema}
                onSubmit={(values, actions) => submitCheck(values, actions)}
                render={({ isValid, errors, touched, handleChange, handleBlur, setFieldValue, values }) => {
                  const dobError = errors.dob && errors.dob !== 'Invalid Date' && errors.dob.indexOf('dob') !== 0 ? errors.dob : null;

                  return (
                    <Form>
                      <Box>
                        <FormField mb={3} error={touched.email && errors.email}>
                          <>
                            <TextInput
                              // placeholder="Your Email Address"
                              type="email"
                              name="email"
                              id="input_email"
                              value={email}
                              disabled
                            />
                          </>
                        </FormField>

                        <FormField mb={3} error={touched.password && errors.password}>
                          <TextInput placeholder="Your Password" type="password" name="password" id="input_password" />
                        </FormField>

                        <FormField mb={4} error={touched.dob && dobError}>
                          <MaskInput name="dob" placeholder="Your Date of Birth (MM/DD/YYYY)" mask="99/99/9999" value={values.dob} onChange={handleChange} onBlur={handleBlur} id="input_dob" />
                        </FormField>

                        <FormField flexDirection="row" mb={4} error={touched.consent && errors.consent}>
                          <CheckboxInput name="consent" />
                          <Text fontSize={1}>
                            I agree to Paloma Health's&nbsp;
                            <StyledLink href="https://www.palomahealth.com/legal-doc/paloma-health-terms-of-use" target="_blank">
                              Terms of Use
                            </StyledLink>
                            ,{' '}
                            <StyledLink href="https://www.palomahealth.com/legal-doc/privacy-policy" target="_blank">
                              Privacy Policy
                            </StyledLink>{' '}
                            , and{' '}
                            <StyledLink href="https://www.palomahealth.com/legal-doc/consent-to-telehealth" target="_blank">
                              Consent to Teleahealth
                            </StyledLink>
                          </Text>
                        </FormField>

                        <FormField flexDirection="row" mb={4} error={touched.consenthipaa && errors.consenthipaa}>
                          <CheckboxInput name="consenthipaa" />
                          <Text fontSize={1}>
                            I have received and read Paloma Health’s&nbsp;
                            <StyledLink href="https://www.palomahealth.com/legal-doc/notice-of-privacy-practices" target="_blank">
                              Notice of Privacy Practices
                            </StyledLink>
                          </Text>
                        </FormField>

                        <FormField flexDirection="row" mb={3} error={touched.consentshare && errors.consentshare}>
                          <CheckboxInput name="consentshare" />
                          <Text fontSize={1}>I consent to Paloma Health sharing my medical information with the medical group and providers responsible for my care</Text>
                        </FormField>
                      </Box>

                      <Flex justifyContent="center">
                        <Button variant="primary" width={[1 / 2, 1 / 3]} type="submit" disabled={!isValid} id="btn_next">
                          Next
                        </Button>
                      </Flex>
                    </Form>
                  );
                }}
              />
            </Flex>
          </Card>
        </Flex>
        <Aside />
      </Flex>
    </Fragment>
  );
};

export default compose(
  withApollo,
  withWizard,
)(Basic);
